import { useState } from "react";
import { toast } from "sonner";
import { AlertOctagon, Check, Circle, CircleDot, Loader2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { canEdit, useAuth } from "@/context/AuthContext";
import { useOffline } from "@/context/OfflineContext";
import { DEFAULT_STATUS, DEPARTMENTS, FACTORY_STATUS_COLORS, SHIPPING_METHODS, STATUS_VALUES, statusColor, statusLabel } from "@/constants/workflow";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const REASON_REQUIRED = new Set([
  "design:Hold",
  "design:Revision Required",
  "bim:Revision Required",
  "production:Hold",
  "qaqc:Failed",
]);

const DONE_STATES = {
  design: ["Approved"],
  bim: ["Published"],
  production: ["Completed"],
  qaqc: ["Passed"],
  logistics: ["Arrived"],
  site: [],
};

const IN_PROGRESS = {
  design: ["In Design"],
  bim: ["In Design"],
  production: ["In Production"],
  qaqc: [],
  logistics: ["Shipped"],
  site: [],
};

export function WorkflowProgress({ panel }) {
  return (
    <div data-testid="workflow-progress" className="space-y-1.5">
      {DEPARTMENTS.map((dept) => {
        const value = panel.statuses?.[dept.key];
        const done = DONE_STATES[dept.key].includes(value);
        const active = IN_PROGRESS[dept.key].includes(value);
        return (
          <div key={dept.key} className="flex items-center gap-2 text-xs">
            {done ? (
              <Check size={13} className="text-emerald-600" />
            ) : active ? (
              <CircleDot size={13} className="text-cyan-600" />
            ) : (
              <Circle size={13} className="text-slate-500" />
            )}
            <span className="w-20 text-slate-600">{dept.label}</span>
            <span
              className="mono truncate text-[11px]"
              style={{ color: statusColor(dept.key, value) }}
            >
              {statusLabel(value)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function WorkflowSection({
  projectId,
  panel,
  pallets,
  onChanged,
  readOnly = false,
}) {
  const { capabilities } = useAuth();
  const { submit } = useOffline();
  const [pending, setPending] = useState(null);
  const [reasonFor, setReasonFor] = useState(null);
  const [reason, setReason] = useState("");
  const [arrivalFor, setArrivalFor] = useState(null);
  const [installFor, setInstallFor] = useState(null);
  const [installDate, setInstallDate] = useState("");
  const [arrivalDate, setArrivalDate] = useState(() => new Date().toISOString().slice(0, 10));

  const apply = async (department, value, reasonText, arrival, installedOn) => {
    setPending(department);
    try {
      const result = await submit({
        method: "put",
        path: `/projects/${projectId}/panels/${panel.panel_uid}/status`,
        body: {
          department,
          status: value === "__null__" ? null : value,
          reason: reasonText || null,
          arrival_date: arrival || null,
          installed_on: installedOn || null,
        },
        label: `${panel.panel_name || panel.panel_uid}: ${department.toUpperCase()} → ${value}`,
        panel_uid: panel.panel_uid,
        version: panel.updated_at || null,
      });
      if (!result.ok) toast.error(result.error);
      else if (result.queued)
        toast.success(`Queued offline — ${department.toUpperCase()} → ${value}`);
      else {
        toast.success(`${department.toUpperCase()} → ${value}`);
        onChanged?.();
      }
    } finally {
      setPending(null);
      setReasonFor(null);
      setArrivalFor(null);
      setInstallFor(null);
      setReason("");
    }
  };

  const handleChange = (department, value) => {
    if (department === "logistics" && value === "Arrived") {
      setArrivalDate(panel.delivered_on || new Date().toISOString().slice(0, 10));
      setArrivalFor({ department, value });
      return;
    }
    if (department === "site" && value === "Installed") {
      setInstallDate(panel.installed_on || "");
      setInstallFor({ department, value });
      return;
    }
    if (REASON_REQUIRED.has(`${department}:${value}`)) {
      setReasonFor({ department, value });
      return;
    }
    apply(department, value);
  };

  return (
    <div data-testid="workflow-section" className="space-y-4">
      {panel.workflow_blocked ? (
        <div
          data-testid="workflow-blocked-banner"
          className="rounded-lg border border-red-500/35 bg-red-500/10 p-3"
        >
          <p className="mono flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-red-700">
            <AlertOctagon size={13} /> Workflow blocked
          </p>
          <p className="mt-1.5 text-xs text-red-700">
            Blocked by <b>{panel.blocked_by}</b> — {panel.block_reason}
          </p>
        </div>
      ) : null}

      {DEPARTMENTS.map((dept) => {
        const editable =
          !readOnly && dept.key !== "production" && canEdit(capabilities, dept.key);
        const value = panel.statuses?.[dept.key];
        const allowed = panel.allowed_transitions?.[dept.key];
        return (
          <div key={dept.key}>
            <div className="flex items-center justify-between">
              <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
                {dept.label}
              </p>
              <div className="flex items-center gap-2">
                <span
                  data-testid={
                    readOnly ? `status-readonly-${dept.key}` : `status-current-${dept.key}`
                  }
                  className="mono text-[11px]"
                  style={{ color: statusColor(dept.key, value) }}
                >
                  {statusLabel(value)}
                </span>
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: statusColor(dept.key, value) }}
                />
              </div>
            </div>
            {editable ? (
              <Select
                value={value == null ? DEFAULT_STATUS[dept.key] : value}
                onValueChange={(next) => handleChange(dept.key, next)}
                disabled={pending === dept.key}
              >
                <SelectTrigger
                  data-testid={`status-select-${dept.key}`}
                  className="mt-1.5 h-9 border-slate-200 bg-[#EEF2F8] text-xs text-slate-800"
                >
                  {pending === dept.key ? (
                    <Loader2 size={13} className="animate-spin" />
                  ) : (
                    <SelectValue placeholder="—" />
                  )}
                </SelectTrigger>
                <SelectContent className="border-slate-200 bg-white text-slate-800">
                  {STATUS_VALUES[dept.key].map((option) => {
                    const disabled = allowed ? !allowed.includes(option) : false;
                    return (
                      <SelectItem
                        key={option}
                        value={option}
                        disabled={disabled}
                        data-testid={`status-option-${dept.key}-${option.replace(/[^\w]/g, "-")}`}
                        className="text-xs"
                      >
                        {option}
                        {disabled ? " (blocked)" : ""}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : null}
            {dept.key === "production" ? (
              panel.statuses?.production === "In Production" && panel.factory_status ? (
                <p
                  data-testid="production-factory-status"
                  className="mono mt-1 flex items-center gap-1.5 text-[9.5px] uppercase tracking-[0.18em] text-slate-500"
                >
                  Factory:
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{
                      backgroundColor:
                        FACTORY_STATUS_COLORS[panel.factory_status] || "#94A3B8",
                    }}
                  />
                  <span
                    style={{
                      color: FACTORY_STATUS_COLORS[panel.factory_status] || "#64748B",
                    }}
                  >
                    {panel.factory_status}
                  </span>
                </p>
              ) : (
                <p
                  data-testid="production-factory-hint"
                  className="mono mt-1 text-[9.5px] uppercase tracking-[0.18em] text-slate-400"
                >
                  Managed in Factory
                </p>
              )
            ) : null}
            {dept.key === "logistics" ? (
              <LogisticsFields
                projectId={projectId}
                panel={panel}
                pallets={pallets}
                onChanged={onChanged}
                readOnly={readOnly}
              />
            ) : null}
            {dept.key === "site" && panel.statuses?.site === "Installed" ? (
              <p
                data-testid="installed-on-value"
                className="mono mt-1 pl-6 text-[10.5px] text-slate-500"
              >
                Installed on {fmtPanelDate(panel.installed_on)}
              </p>
            ) : null}
          </div>
        );
      })}

      <Dialog open={!!reasonFor} onOpenChange={(open) => !open && setReasonFor(null)}>
        <DialogContent className="border-slate-200 bg-white text-slate-900">
          <DialogHeader>
            <DialogTitle className="font-display text-lg">
              Reason required — {reasonFor?.value}
            </DialogTitle>
          </DialogHeader>
          <Textarea
            data-testid="status-reason-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Explain why this status is being set…"
            className="min-h-24 border-slate-200 bg-[#EEF2F8] text-sm"
          />
          <Button
            data-testid="status-reason-submit"
            disabled={!reason.trim()}
            onClick={() => apply(reasonFor.department, reasonFor.value, reason)}
            className="rounded-full bg-cyan-500 text-[#04121a] hover:bg-cyan-400"
          >
            Confirm status change
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog open={!!installFor} onOpenChange={(open) => !open && setInstallFor(null)}>
        <DialogContent
          data-testid="install-date-dialog"
          className="max-w-sm border-slate-200 bg-white text-slate-900"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-lg">Installation date</DialogTitle>
          </DialogHeader>
          <p className="text-[11.5px] text-slate-500">
            Pick the day the panel was actually installed on site — this date is stored, not
            today&apos;s date.
          </p>
          <input
            data-testid="install-date-input"
            type="date"
            value={installDate}
            onChange={(e) => setInstallDate(e.target.value)}
            className="mono h-10 w-full rounded-md border border-slate-200 bg-[#EEF2F8] px-2 text-sm"
          />
          <Button
            data-testid="install-date-submit"
            disabled={!installDate}
            onClick={() => apply(installFor.department, installFor.value, null, null, installDate)}
            className="rounded-full bg-cyan-500 text-[#04121a] hover:bg-cyan-400"
          >
            Mark as Installed
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
const fmtPanelDate = (value) => {
  if (!value) return "—";
  if (value === "Varies") return "Varies";
  return new Date(value).toLocaleDateString();
};

/** Logistics detail block: Shipping Method → Shipment Code → Pallet → ETA. */
function LogisticsFields({ projectId, panel, pallets, onChanged, readOnly = false }) {
  const { capabilities, user } = useAuth();
  const allowed =
    !readOnly &&
    (capabilities?.is_admin ||
      (capabilities?.data_editors?.pallet || []).some((r) => (user?.roles || []).includes(r)));
  const shipped = panel.statuses?.logistics === "Shipped";
  const [code, setCode] = useState(panel.shipment_code || "");
  const [saving, setSaving] = useState(false);

  const save = async (method, shipmentCode) => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/panels/${panel.panel_uid}/shipping`, {
        shipping_method: method,
        shipment_code: shipmentCode,
        shipped_on: panel.shipped_on || new Date().toISOString().slice(0, 10),
      });
      toast.success("Shipping details updated");
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 space-y-2" data-testid="logistics-fields">
      {shipped ? (
        <div>
          <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Shipping Method
          </p>
          {allowed ? (
            <Select
              value={panel.shipping_method || "__none__"}
              onValueChange={(next) => save(next === "__none__" ? null : next, code)}
              disabled={saving}
            >
              <SelectTrigger
                data-testid="shipping-method-select"
                className="mt-1.5 h-9 border-slate-200 bg-[#EEF2F8] text-xs"
              >
                <SelectValue placeholder="Select method" />
              </SelectTrigger>
              <SelectContent className="border-slate-200 bg-white text-slate-800">
                <SelectItem value="__none__" className="text-xs">
                  —
                </SelectItem>
                {SHIPPING_METHODS.map((method) => (
                  <SelectItem
                    key={method}
                    value={method}
                    data-testid={`shipping-method-${method.replace(/\s/g, "-")}`}
                    className="text-xs"
                  >
                    {method}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <p className="mono mt-1.5 text-xs text-slate-700">{panel.shipping_method || "—"}</p>
          )}
        </div>
      ) : null}

      <div>
        <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Shipment Code</p>
        {allowed ? (
          <input
            data-testid="shipment-code-input"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onBlur={() =>
              (code || "") !== (panel.shipment_code || "") && save(panel.shipping_method, code)
            }
            placeholder="SHP-001"
            className="mono mt-1.5 h-9 w-full rounded-md border border-slate-200 bg-[#EEF2F8] px-2 text-xs text-slate-800"
          />
        ) : (
          <p className="mono mt-1.5 text-xs text-slate-700">{panel.shipment_code || "—"}</p>
        )}
      </div>

      <PalletField
        projectId={projectId}
        panel={panel}
        pallets={pallets}
        onChanged={onChanged}
        readOnly={readOnly}
      />

      <div>
        <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
          Estimated Time of Arrival (ETA)
        </p>
        <p data-testid="estimated-arrival" className="mono mt-1.5 text-xs text-slate-700">
          {fmtPanelDate(panel.estimated_arrival)}
        </p>
        {panel.shipped_at ? (
          <p className="mt-0.5 text-[10px] text-slate-500">
            Shipped {fmtPanelDate(panel.shipped_at)}
            {panel.shipping_method ? ` · ${panel.shipping_method}` : ""}
          </p>
        ) : null}
      </div>

      {panel.statuses?.logistics === "Arrived" ? (
        <div>
          <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Arrival Date
          </p>
          <p data-testid="arrival-date-value" className="mono mt-1.5 text-xs text-slate-700">
            {fmtPanelDate(panel.delivered_on)}
          </p>
        </div>
      ) : null}
    </div>
  );
}

function PalletField({ projectId, panel, pallets, onChanged, readOnly = false }) {
  const { capabilities, user } = useAuth();
  const allowed =
    !readOnly &&
    !capabilities?.is_guest &&
    (capabilities?.is_admin ||
      (capabilities?.data_editors?.pallet || []).some((r) => (user?.roles || []).includes(r)));
  const [creating, setCreating] = useState(false);
  const [code, setCode] = useState("");

  const assign = async (palletId) => {
    try {
      await api.put(`/projects/${projectId}/pallets/assign`, {
        pallet_id: palletId === "__none__" ? null : palletId,
        panel_uids: [panel.panel_uid],
      });
      toast.success("Pallet updated");
      onChanged?.();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const createPallet = async () => {
    try {
      const { data } = await api.post(`/projects/${projectId}/pallets`, { code });
      setCode("");
      setCreating(false);
      await assign(data.id);
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  return (
    <div className="mt-2">
      <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Pallet</p>      {allowed ? (
        <>
          <Select value={panel.pallet_id || "__none__"} onValueChange={assign}>
            <SelectTrigger
              data-testid="pallet-select"
              className="mt-1.5 h-9 border-slate-200 bg-[#EEF2F8] text-xs"
            >
              <SelectValue placeholder="Select pallet" />
            </SelectTrigger>
            <SelectContent className="border-slate-200 bg-white text-slate-800">
              <SelectItem value="__none__" className="text-xs">
                — no pallet —
              </SelectItem>
              {pallets.map((pallet) => (
                <SelectItem key={pallet.id} value={pallet.id} className="mono text-xs">
                  {pallet.code} ({pallet.panel_count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {creating ? (
            <div className="mt-2 flex gap-2">
              <input
                data-testid="new-pallet-code-input"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="PLT-035"
                className="mono h-8 w-full rounded-md border border-slate-200 bg-[#EEF2F8] px-2 text-xs text-slate-800"
              />
              <Button
                data-testid="new-pallet-save"
                onClick={createPallet}
                className="h-8 rounded-md bg-cyan-500 px-3 text-xs text-[#04121a]"
              >
                Save
              </Button>
            </div>
          ) : (
            <button
              data-testid="new-pallet-button"
              onClick={() => setCreating(true)}
              className={`mono mt-2 text-[10px] uppercase tracking-wider text-cyan-600 hover:underline ${
                capabilities?.is_guest ? "hidden" : ""
              }`}
            >
              + Create new pallet
            </button>
          )}
        </>
      ) : (
        <p className="mono mt-1.5 text-xs text-slate-700">{panel.pallet_code || "—"}</p>
      )}
    </div>
  );
}
